import axios, { AxiosError } from "axios";

export const api = axios.create({
  baseURL: "/api",          // Vite dev-server proxies /api → http://localhost:5022
  withCredentials: true,
  timeout: 15_000,
});

api.interceptors.response.use(
  (r) => r,
  (err: AxiosError<{ ok: false; error?: { message: string; code?: string } }>) => {
    // 401 = the session is gone. 403 ACCOUNT_INACTIVE = the account was blocked
    // or suspended mid-session; the server has just revoked the session, so
    // treat it the same and send the person to /login rather than leaving the
    // portal sitting there throwing errors.
    const inactive = err.response?.status === 403
      && err.response?.data?.error?.code === "ACCOUNT_INACTIVE";
    if (err.response?.status === 401 || inactive) {
      // Clear cached user; routing will redirect to /login.
      try {
        void import("./query").then(({ queryClient }) => {
          queryClient.removeQueries({ queryKey: ["me"] });
          if (inactive) queryClient.clear();
        });
      } catch {/* circular guard */}
      if (inactive && typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.assign("/login?reason=inactive");
      }
    }
    return Promise.reject(err);
  }
);

export interface ApiOk<T> { ok: true; data: T }
export interface ApiErr { ok: false; error: { message: string; code?: string } }
export type ApiResult<T> = ApiOk<T> | ApiErr;

export function unwrap<T>(payload: ApiResult<T>): T {
  if (!payload.ok) throw new Error(payload.error.message);
  return payload.data;
}

export function describeError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiErr | undefined;
    return data?.error?.message ?? err.message ?? "Network error";
  }
  return (err as Error)?.message ?? "Unexpected error";
}
